import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Upload, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";

/**
 * Ajustes de empresa: datos que aparecen en los PDF de cotización/remisión/comprobante.
 * Solo director puede editar.
 */
export default function EmpresaPage() {
  const [form, setForm] = useState({
    nombre: "", nit: "", regimen_fiscal: "Régimen común",
    direccion: "", barrio: "", ciudad: "", departamento: "",
    telefono: "", telefono2: "", whatsapp: "",
    email: "", eslogan: "", logo_url: "", logo_key: "",
    umbral_flete_gratis: 600000,
    pdf_config: {
      rotulo: { nit: true, direccion: true, barrio: true, ciudad: true, telefono: true, telefono2: false, whatsapp: false, email: false, eslogan: false },
      remision: { nit: true, regimen: true, direccion: true, barrio: false, ciudad: true, telefono: true, telefono2: false, whatsapp: false, email: true, eslogan: false },
    },
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    // Feb/2026 (fix logo desapareciendo en dispositivos nuevos):
    // Intentamos cargar el logo optimistamente en paralelo al GET /empresa
    // — así, en dispositivos nuevos, no vemos un flash de "sin logo" mientras
    // la API responde. Si el endpoint 404's (no hay logo), el img onError
    // marca el estado como "sin logo" y mostramos el placeholder.
    setLogoPreview(`${api.defaults.baseURL}/pedidos/_/empresa/logo?_=${Date.now()}`);
    setLogoLoading(true);
    setLogoError(false);

    api.get("/pedidos/_/empresa").then(({ data }) => {
      setForm((f) => {
        const merged = { ...f, ...data };
        // Deep-merge pdf_config para que si el backend devuelve un objeto
        // vacío no borre los defaults del panel de checkboxes.
        const backendCfg = data.pdf_config || {};
        merged.pdf_config = {
          rotulo: { ...f.pdf_config.rotulo, ...(backendCfg.rotulo || {}) },
          remision: { ...f.pdf_config.remision, ...(backendCfg.remision || {}) },
        };
        return merged;
      });
      // Si el backend confirma que NO hay logo, limpiamos el preview.
      if (!data.logo_key) {
        setLogoPreview(null);
        setLogoLoading(false);
      }
    }).catch(() => {
      setLogoLoading(false);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Excluimos logo_url/logo_key del PUT: se gestionan por su propio endpoint
      // (POST /pedidos/_/empresa/logo). Si los incluyéramos aquí y por race
      // condition estuvieran "" antes del GET inicial, borraríamos el logo real.
      const { logo_url, logo_key, ...payload } = form;
      await api.put("/pedidos/_/empresa", payload);
      toast.success("Datos de empresa actualizados. Aparecerán en los próximos PDF.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const onLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo muy grande (máx 2 MB)"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pedidos/_/empresa/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.logo_url || "", logo_key: data.logo_key || "" }));
      setLogoPreview(`${api.defaults.baseURL}/pedidos/_/empresa/logo?_=${Date.now()}`);
      setLogoError(false);
      setLogoLoading(true);
      toast.success("Logo actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir el logo");
    } finally { setUploadingLogo(false); }
  };

  return (
    <div>
      <header className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Configuración</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-primary" /> Datos de empresa
        </h1>
        <p className="text-muted-foreground mt-2">Aparecen en todos los PDF: cotizaciones, remisiones y comprobantes. Puedes actualizarlos cuando quieras.</p>
      </header>

      <UsageTip storageKey="empresa" steps={[
        "El <b>logo</b> aparece en la banda rose superior de cada PDF (usa PNG/JPG transparente < 2 MB).",
        "Cambia el <b>NIT y teléfonos</b> y los próximos PDF los usarán automáticamente — los ya emitidos no se re-emiten.",
        "Las <b>remisiones</b> generan un consecutivo automático tipo <b>REM-2026-000001</b> al descargarlas.",
      ]} />

      <form onSubmit={save} className="bg-card border border-border rounded-xl p-6 space-y-6 max-w-4xl">
        {/* Logo */}
        <div className="flex items-center gap-6">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden relative">
            {logoPreview && !logoError ? (
              <>
                <img
                  src={logoPreview}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain"
                  onLoad={() => { setLogoLoading(false); setLogoError(false); }}
                  onError={() => { setLogoLoading(false); setLogoError(true); setLogoPreview(null); }}
                  data-testid="empresa-logo-img"
                />
                {logoLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/40">
                    <div className="w-6 h-6 border-2 border-primary/60 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </>
            ) : (
              <ImageIcon className="w-10 h-10 text-muted-foreground/40" />
            )}
          </div>
          <div>
            <Label>Logo de la empresa</Label>
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadingLogo} data-testid="empresa-logo-upload">
                <Upload className="w-4 h-4 mr-2" /> {uploadingLogo ? "Subiendo..." : (logoPreview && !logoError) ? "Cambiar logo" : "Subir logo"}
              </Button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => onLogo(e.target.files?.[0])} className="hidden" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">PNG/JPG/WEBP, máximo 2 MB. Se muestra en la barra superior de cada PDF.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Razón social / Nombre*</Label>
            <Input required value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-11 mt-1" placeholder="Ej: Beem Importaciones S.A.S." data-testid="empresa-nombre" />
          </div>
          <div>
            <Label>NIT (con dígito de verificación)</Label>
            <Input value={form.nit || ""} onChange={(e) => setForm({ ...form, nit: e.target.value })} className="h-11 mt-1" placeholder="Ej: 900.123.456-7" data-testid="empresa-nit" />
          </div>
          <div className="col-span-2">
            <Label>Régimen fiscal</Label>
            <select
              value={form.regimen_fiscal || "Régimen común"}
              onChange={(e) => setForm({ ...form, regimen_fiscal: e.target.value })}
              className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm"
              data-testid="empresa-regimen"
            >
              <option value="Régimen común">Régimen común</option>
              <option value="Régimen simplificado">Régimen simplificado</option>
              <option value="Régimen simple de tributación">Régimen simple de tributación</option>
              <option value="No responsable de IVA">No responsable de IVA</option>
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">Aparece en el encabezado de todos los PDF.</p>
          </div>
          <div className="col-span-2">
            <Label>Dirección</Label>
            <Input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className="h-11 mt-1" placeholder="Cra 45 #90-25, oficina 501" data-testid="empresa-direccion" />
          </div>
          <div>
            <Label>Barrio</Label>
            <Input value={form.barrio || ""} onChange={(e) => setForm({ ...form, barrio: e.target.value })} className="h-11 mt-1" placeholder="El Recuerdo" data-testid="empresa-barrio" />
          </div>
          <div>
            <Label>Ciudad</Label>
            <Input value={form.ciudad || ""} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} className="h-11 mt-1" placeholder="Medellín" data-testid="empresa-ciudad" />
          </div>
          <div>
            <Label>Departamento</Label>
            <Input value={form.departamento || ""} onChange={(e) => setForm({ ...form, departamento: e.target.value })} className="h-11 mt-1" placeholder="Antioquia" data-testid="empresa-departamento" />
            <p className="text-[10px] text-muted-foreground mt-1">Aparece junto a la ciudad en los PDF (ej. "Medellín, Antioquia") cuando el checkbox de "Ciudad" está activo.</p>
          </div>
          <div>
            <Label>Correo</Label>
            <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" placeholder="ventas@beem.moda" data-testid="empresa-email" />
          </div>
          <div>
            <Label>Teléfono principal</Label>
            <Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-11 mt-1" placeholder="+57 604 555 1234" data-testid="empresa-tel1" />
          </div>
          <div>
            <Label>Teléfono secundario</Label>
            <Input value={form.telefono2 || ""} onChange={(e) => setForm({ ...form, telefono2: e.target.value })} className="h-11 mt-1" placeholder="+57 320 555 0000" data-testid="empresa-tel2" />
          </div>
          <div>
            <Label>WhatsApp</Label>
            <Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="h-11 mt-1" placeholder="+57 320 555 0000" data-testid="empresa-whatsapp" />
          </div>
          <div>
            <Label>Eslogan (opcional)</Label>
            <Input value={form.eslogan || ""} onChange={(e) => setForm({ ...form, eslogan: e.target.value })} className="h-11 mt-1" placeholder="Insumos que enamoran" data-testid="empresa-eslogan" />
          </div>
          <div className="col-span-2">
            <Label>Umbral de flete gratis (COP)</Label>
            <Input
              type="number"
              min="0"
              step="1000"
              value={form.umbral_flete_gratis ?? 600000}
              onChange={(e) => setForm({ ...form, umbral_flete_gratis: parseInt(e.target.value || "0", 10) })}
              className="h-11 mt-1"
              placeholder="600000"
              data-testid="empresa-umbral-flete"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Los pedidos por encima de este valor van con flete cubierto por Beem.
              Los pedidos por debajo, si Beem pagó el flete al despachar, se cobran
              al cliente automáticamente en la factura.
            </p>
          </div>
        </div>

        {/* Panel: Qué info aparece en cada documento */}
        <div className="mt-6 rounded-xl border border-border/60 bg-card/50 p-5" data-testid="empresa-pdf-config">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Building2 className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-primary">Personalización de documentos</div>
              <div className="text-sm font-semibold text-foreground">¿Qué info de tu empresa aparece en cada PDF?</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Marca las casillas para que ese dato aparezca en el documento correspondiente.
            El nombre siempre aparece. Cambios aplican a los próximos PDF generados.
          </p>
          {[
            { doc: "rotulo", label: "Rótulo (etiqueta de envío)", fields: [
              ["nit", "NIT"],
              ["direccion", "Dirección"],
              ["barrio", "Barrio"],
              ["ciudad", "Ciudad"],
              ["telefono", "Teléfono principal"],
              ["telefono2", "Teléfono alterno"],
              ["whatsapp", "WhatsApp"],
              ["email", "Email"],
              ["eslogan", "Eslogan"],
            ]},
            { doc: "remision", label: "Remisión / Cotización", fields: [
              ["nit", "NIT"],
              ["regimen", "Régimen fiscal"],
              ["direccion", "Dirección"],
              ["barrio", "Barrio"],
              ["ciudad", "Ciudad"],
              ["telefono", "Teléfono principal"],
              ["telefono2", "Teléfono alterno"],
              ["whatsapp", "WhatsApp"],
              ["email", "Email"],
              ["eslogan", "Eslogan"],
            ]},
          ].map((section) => (
            <div key={section.doc} className="mb-3 last:mb-0">
              <div className="text-xs font-bold text-foreground/80 mb-2">{section.label}</div>
              <div className="flex flex-wrap gap-2">
                {section.fields.map(([key, name]) => {
                  const cfg = form.pdf_config?.[section.doc] || {};
                  const checked = cfg[key] !== false; // default true
                  return (
                    <label key={key}
                      className={`flex items-center gap-2 px-3 h-10 rounded-full border text-sm cursor-pointer transition ${checked ? "bg-primary/10 border-primary/40 text-primary" : "bg-card border-border text-muted-foreground hover:bg-accent"}`}
                      data-testid={`empresa-pdf-${section.doc}-${key}`}>
                      <input type="checkbox" className="accent-primary"
                        checked={checked}
                        onChange={(e) => setForm({
                          ...form,
                          pdf_config: {
                            ...(form.pdf_config || {}),
                            [section.doc]: { ...(form.pdf_config?.[section.doc] || {}), [key]: e.target.checked },
                          },
                        })} />
                      {name}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90 h-11 px-6" data-testid="empresa-save">
            <Check className="w-4 h-4 mr-2" /> {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </form>
    </div>
  );
}
